/**
 * Componentes reutilizables de inputs con contador de caracteres
 * 
 * Estos componentes facilitan la creación de inputs con contador de caracteres
 * de forma consistente en toda la aplicación.
 * 
 * @example
 * // Import individual
 * import { TextFieldWithCounter } from 'components/ui/commons/inputs';
 * 
 * @example
 * // Import múltiple
 * import { TextFieldWithCounter, InputWithCounter } from 'components/ui/commons/inputs';
 */

import { useState } from 'react';
import LocaleFormat from '../../../../commons/utils/LocaleFormat.js';

export { default as TextFieldWithCounter } from './TextFieldWithCounter';
export { default as InputWithCounter } from './InputWithCounter';

/**
 * Utilidad para limitar la cantidad de dígitos de montos numéricos.
 * Por defecto limita a 11 dígitos en la parte entera y 2 en la decimal.
 */
export const limitMontoDigits = (value, maxIntegerDigits = 11, maxDecimalDigits = 2) => {
	if (value === '' || value === null || value === undefined) {
		return value;
	}
	let str = value.toString();
	// Solo dígitos y un punto decimal
	str = str.replace(/[^\d.]/g, '');
	const parts = str.split('.');
	let intPart = parts[0] || '';
	let decPart = parts[1] || '';
	intPart = intPart.slice(0, maxIntegerDigits);
	decPart = decPart.slice(0, maxDecimalDigits);
	return decPart.length ? `${intPart}.${decPart}` : intPart;
};

/**
 * Hook para inputs de montos con separadores de miles/decimales (LocaleFormat.getPriceSeparators).
 * Mientras el input tiene foco muestra lo que se tipea; al salir lo muestra formateado con formatPrice.
 * onChange recibe el monto en formato canónico ('1234.5', o '' si se vacía), ya limitado por limitMontoDigits.
 * Devuelve props para spreadear en un <input> o un TextField: {...usePriceInput(valor, onChange)}.
 */
export const usePriceInput = (value, onChange) => {
	const [texto, setTexto] = useState(null);
	const formateado = value === '' || value === null || value === undefined ? '' : LocaleFormat.formatPrice(value);
	return {
		type: 'text',
		value: texto !== null ? texto : formateado,
		onFocus: () => setTexto(formateado),
		onBlur: () => setTexto(null),
		onChange: (e) => {
			const nuevo = e.target.value;
			if (nuevo.trim() === '') {
				setTexto(nuevo);
				onChange('');
				return;
			}
			const numero = LocaleFormat.parsePriceToNumber(nuevo);
			if (isNaN(numero) || numero < 0) return;
			const canonico = String(numero);
			if (limitMontoDigits(canonico) !== canonico) return;
			setTexto(nuevo);
			onChange(canonico);
		},
	};
};
